import { useState } from 'react';
import type { JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Zap, Users, KeyRound } from 'lucide-react';
import { fetchLocations, updateLocation, type Location, type PaginatedResponse } from '@/lib/api';
import {
  ceilingMessageFor, flatRateMaxCents, isRateTextOverCeiling,
  rateTextToCents, rateTextToStoredCents, storedCentsToRateText,
} from '@/lib/siteElectricityInput';
import { needsRateConfirmStep } from '@/lib/siteElectricityInput';
import { RateConfirmStep } from '@/components/RateConfirmStep';
import { LocationResidentManagedCard } from '@/components/ui/LocationResidentManagedCard';
import { LocationFobRegistrationCard } from '@/components/ui/LocationFobRegistrationCard';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { SitePicker, useSelectedSite } from './SitePicker';
import styles from './SitePages.module.css';
import { LocationBankCard } from './LocationBankCard';
import { SiteHostStatement } from '@/components/ui/SiteHostStatement';
import { BuildingStaffInvitesCard } from '@/components/ui/BuildingStaffInvitesCard';
import { useAuthStore } from '@/store/auth';

/** Site settings, including a location-specific Stripe bank connection. */
export function SiteSettingsPage(): JSX.Element {
  useRecentRoute('Site settings');
  const qc = useQueryClient();
  const userId = useAuthStore((state) => state.user?._id);
  const locationKey = ['site-settings', 'locations', userId];

  const { data: locations, isLoading, isError, refetch } = useQuery({
    queryKey: locationKey,
    // Server-scoped: the third_party_admin branch of dashboardScope
    // narrows this to the caller's AccessAssignment.location_ids.
    queryFn: async () => {
      const first = await fetchLocations({ limit: 100 });
      const data = [...first.data];
      for (let page = 2; page <= first.meta.pages; page++) data.push(...(await fetchLocations({ limit: 100, page })).data);
      return { ...first, data };
    },
  });
  const sites = locations?.data ?? [];
  const { selectedId, setSelectedId, selected } = useSelectedSite(sites);

  const [rate, setRate] = useState<string>('');
  const [rateDirty, setRateDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);

  const storedRateCents = selected?.electricity_rate_cents ?? 0;
  // As stored, to a hundredth of a cent (Warren, 21 October 2026, night: "43.63 stays 43.63"): 0.4363, 0.17.
  const shownRate = rateDirty ? rate : (storedRateCents ? storedCentsToRateText(storedRateCents, 'dollars') : '');
  // The Site electricity ceiling for this Location's flat rate, checked on
  // the typed dollars scaled to cents, before rounding. The cap follows the
  // Location's electricity_rate_type, the same pick the API makes
  // (validateLocationReimbursement): 60¢ per kWh, above 40 with the second
  // step (Warren, 21 October 2026, night; 40 since 2026-09-27: "a flat rate
  // above that is a typo or a lie"), 80¢ per hour. This page
  // saves only the rate, so the API judges it by the STORED type; a fixed
  // 40 here refused a valid 55¢/hour rate the API would accept. Absent
  // (a legacy doc) reads as per_kwh, the schema default. The API refuses
  // a rate above the cap, so say so as the rate is typed and hold Save.
  const perHour = selected?.electricity_rate_type === 'per_hour';
  const maxCents = flatRateMaxCents(selected?.electricity_rate_type);
  const overCeiling = isRateTextOverCeiling(shownRate, 'dollars', maxCents);
  // Warren, 21 October 2026, night: "raise the utility and reimbursement caps to 60 cents, with a second confirmation
  // above 40". A rate per kWh above 40 (the cents it saves, to a hundredth) shows the second step before it saves.
  const centsToSave = rateTextToStoredCents(shownRate, 'dollars');
  const needsConfirmStep = needsRateConfirmStep(centsToSave, selected?.electricity_rate_type,
    { cents: selected?.electricity_rate_cents, rateType: selected?.electricity_rate_type });
  const [confirmCents, setConfirmCents] = useState<number | null>(null);
  const confirmStepShown = needsConfirmStep && confirmCents === centsToSave;

  async function saveRate(confirmed = false): Promise<void> {
    if (!selected) return;
    if (rateTextToCents(shownRate, 'dollars') === null) {
      setSaveError(perHour
        ? 'Enter the price per hour your building charges, like 0.50'
        : 'Enter the price per kilowatt-hour your utility charges, like 0.17');
      return;
    }
    if (overCeiling) return;
    if (needsConfirmStep && !confirmed) { setConfirmCents(centsToSave); return; }
    setSaving(true);
    setSaveError('');
    setSaved(false);
    try {
      await updateLocation(selected._id, { electricity_rate_cents: centsToSave, ...(confirmed && needsConfirmStep ? { confirm_above_40: true } : {}) });
      await qc.invalidateQueries({ queryKey: ['site-settings', 'locations'] });
      setRateDirty(false);
      setConfirmCents(null);
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the rate.');
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) return <div className={styles.page}><p className={styles.muted}>Loading…</p></div>;
  if (isError) return <div className={styles.page} role="alert"><p>Could not load your locations.</p><button onClick={() => void refetch()}>Retry</button></div>;

  if (!selected) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Settings</h1>
        <p className={styles.muted}>
          No site is assigned to your account yet. Ask your operator to add you to a site.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
        <SitePicker sites={sites} selectedId={selectedId} onSelect={(id) => {
          setSelectedId(id); setRateDirty(false); setRate(''); setSaved(false); setSaveError(''); setConfirmCents(null);
        }} />
      </header>

      {/* ── Electricity rate ─────────────────────────────────────── */}
      <section className={styles.card}>
        <div className={styles.cardHead}>
          <Zap size={18} aria-hidden />
          <h2 className={styles.cardTitle}>Electricity rate</h2>
        </div>
        <p className={styles.cardBody}>
          {perHour
            ? 'What your building is reimbursed per hour of charging. Every guest session at this site reimburses you at this rate before anything else is split.'
            : 'What your building pays per kilowatt-hour. Every guest session at this site reimburses you at this rate before anything else is split, so it should match your utility bill — including delivery and regulatory charges, not just the energy line.'}
        </p>
        <div className={styles.field}>
          <span className={styles.prefix}>$</span>
          <input
            className={styles.input}
            inputMode="decimal"
            value={shownRate}
            placeholder="0.17"
            onChange={(e) => { setRate(e.target.value); setRateDirty(true); setSaved(false); }}
            aria-label={perHour
              ? 'Electricity rate in dollars per hour'
              : 'Electricity rate in dollars per kilowatt-hour'}
            aria-invalid={overCeiling || undefined}
            aria-describedby={overCeiling ? 'site-rate-ceiling-error' : undefined}
          />
          {/* The unit the Location is billed in (electricity_rate_type). */}
          <span className={styles.suffix}>{perHour ? '/hour' : '/kWh'}</span>
          <button
            type="button"
            className={styles.primary}
            onClick={() => void saveRate()}
            disabled={saving || !rateDirty || overCeiling || confirmStepShown}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        {confirmStepShown && (
          <RateConfirmStep cents={centsToSave} busy={saving} buttonClassName={styles.primary}
            onConfirm={() => void saveRate(true)} onCancel={() => setConfirmCents(null)} />
        )}
        {/* The field takes dollars ($ prefix), so the message names $0.60
            (per kWh) or $0.80 (per hour): the same cap the check used. */}
        {overCeiling && (
          <p id="site-rate-ceiling-error" className={styles.error} role="alert">
            {ceilingMessageFor('dollars', maxCents)}
          </p>
        )}
        {saveError && <p className={styles.error}>{saveError}</p>}
        {saved && <p className={styles.ok}>Saved.</p>}
        {storedRateCents === 0 && !rateDirty && (
          <p className={styles.warn}>
            No rate is set. Residents cannot open a plug to guests until there is one.
          </p>
        )}
      </section>

      {/* ── Resident charging ────────────────────────────────────── */}
      <section className={styles.card}>
        <div className={styles.cardHead}>
          <Users size={18} aria-hidden />
          <h2 className={styles.cardTitle}>Resident charging</h2>
        </div>
        <LocationResidentManagedCard
          locationId={selected._id}
          value={selected.allow_resident_managed_chargers ?? false}
          onChange={() => void qc.invalidateQueries({ queryKey: ['site-settings', 'locations'] })}
        />
      </section>

      {/* ── Resident fob registration (F3) ───────────────────────── */}
      <section className={styles.card}>
        <div className={styles.cardHead}>
          <KeyRound size={18} aria-hidden />
          <h2 className={styles.cardTitle}>Fob self-registration</h2>
        </div>
        <LocationFobRegistrationCard
          locationId={selected._id}
          locationTenantId={selected.tenant_id}
          value={selected.allow_resident_fob_registration === true}
          onChange={(next) => {
            // Write the saved value into the cached list BEFORE refetching.
            // The card resyncs from its value prop once the save settles, so
            // with invalidate alone it snapped back to the old cached value
            // until the refetch landed (and stayed wrong if it failed).
            qc.setQueryData<PaginatedResponse<Location>>(locationKey, (old) => old
              ? { ...old, data: old.data.map((l) => (l._id === selected._id ? { ...l, allow_resident_fob_registration: next } : l)) }
              : old);
            void qc.invalidateQueries({ queryKey: ['site-settings', 'locations'] });
          }}
        />
      </section>

      {/* Invite building staff (Warren, 28 Oct 2026, item 5): the site owner's
          own settings; the card hides itself for anyone the Api does not let invite. */}
      <BuildingStaffInvitesCard key={`invites-${selected._id}`} locationId={selected._id} />

      <LocationBankCard key={selected._id} locationId={selected._id} />
      <SiteHostStatement locationId={selected._id} />
    </div>
  );
}

export default SiteSettingsPage;
